import { useMemo } from "react";
import DiaperForm from "../../components/forms/diaperForm";
import DiaperTable from "../../components/tables/diaperTable";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import SectionSummaryBar from "../../components/ui/SectionSummaryBar";
import { useFirestore } from "../../hooks/useFirestore";
import { useChild } from "../../contexts/ChildContext";
import { exportToCSV, diaperCSVColumns } from "../../utils/exportUtils";
import { exportSectionPDF } from "../../utils/pdfUtils";
import { parseItemDate, formatTimeAgo } from "../../utils/dateUtils";

const Diapers = () => {
  const { selectedChild } = useChild();
  const { items: diaperData, addItem, updateItem, deleteItem, loading, error } =
    useFirestore("diapers", selectedChild?.id);

  const diaperStats = useMemo(() => {
    const todayStr = new Date().toDateString();
    const sorted = [...diaperData]
      .map(d => ({ item: d, date: parseItemDate(d) }))
      .filter(x => x.date)
      .sort((a, b) => b.date - a.date);

    const todayItems = sorted.filter(x => x.date.toDateString() === todayStr);
    const count = todayItems.length;
    const wet   = todayItems.filter(x => x.item.type === 'wet'   || x.item.type === 'both').length;
    const dirty = todayItems.filter(x => x.item.type === 'dirty' || x.item.type === 'both').length;
    const lastChangeDate = sorted[0]?.date ?? null;

    const hoursSinceLast = lastChangeDate
      ? (Date.now() - lastChangeDate.getTime()) / 3600000
      : null;
    const isStale = hoursSinceLast !== null && hoursSinceLast >= 4;

    return { count, wet, dirty, lastChangeDate, hoursSinceLast, isStale };
  }, [diaperData]);

  const summaryStats = useMemo(() => {
    if (!diaperData.length) return null;
    return [
      { value: String(diaperStats.count), label: 'today' },
      { value: String(diaperStats.wet),   label: 'wet' },
      { value: String(diaperStats.dirty), label: 'dirty' },
      { value: formatTimeAgo(diaperStats.lastChangeDate) ?? '—', label: 'last change' },
    ];
  }, [diaperData, diaperStats]);

  const handleAdd = async (diaper) => {
    try {
      await addItem({ ...diaper, childId: selectedChild?.id });
    } catch (err) {
      console.error("Error adding diaper entry:", err);
    }
  };

  const handleExport = () =>
    exportToCSV(diaperData, `diapers-${selectedChild?.name || "export"}.csv`, diaperCSVColumns);

  const handleExportPDF = () =>
    exportSectionPDF(diaperData, `diapers-${selectedChild?.name || "export"}.pdf`, diaperCSVColumns, "Nappy Changes", selectedChild?.name || "");

  return (
    <div className="card h-100" id="diapers">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">🧷 Nappy Changes</h5>
          <div className="d-flex gap-1">
            <button className="btn btn-sm btn-outline-secondary" onClick={handleExport}>⬇ CSV</button>
            <button className="btn btn-sm btn-outline-primary" onClick={handleExportPDF}>⬇ PDF</button>
          </div>
        </div>

        {/* Stale nappy alert — shown if no change logged for 4+ hours */}
        {!loading && diaperStats.isStale && (
          <div className="alert alert-warning d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
            <span>⏰</span>
            <span>
              No nappy change logged for <strong>{Math.floor(diaperStats.hoursSinceLast)}h</strong> — is a change due?
            </span>
          </div>
        )}

        {!loading && summaryStats && <SectionSummaryBar stats={summaryStats} />}

        {error && <div className="alert alert-danger">{error}</div>}
        <DiaperForm onAdd={handleAdd} />
        {loading ? (
          <LoadingState message="Loading nappy entries..." />
        ) : diaperData.length > 0 ? (
          <DiaperTable data={diaperData} onUpdate={updateItem} onDelete={deleteItem} />
        ) : (
          <EmptyState
            icon="🧷"
            heading="No nappy changes yet"
            message="You'll be logging these in your sleep soon — add the first one to start."
          />
        )}
      </div>
    </div>
  );
};

export default Diapers;
